import { useCallback, useEffect, useState } from "react";
import { Lightbulb, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { fmtDay } from "../../lib/support";

export const FeatureRequestForm = () => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [why, setWhy] = useState("");
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const [mine, setMine] = useState([]);

  const loadMine = useCallback(async () => {
    try {
      const { data } = await api.get("/support/feature-requests");
      setMine(data.requests || []);
    } catch {
      setMine([]);
    }
  }, []);

  useEffect(() => { loadMine(); }, [loadMine]);

  const submit = async (e) => {
    e.preventDefault();
    if (title.trim().length < 3) return toast.error("Give it a short title");
    if (description.trim().length < 10) return toast.error("Describe it in a bit more detail");
    setBusy(true);
    try {
      const { data } = await api.post("/support/feature-requests", {
        title: title.trim(),
        description: description.trim(),
        why_useful: why.trim() || null,
      });
      setSubmitted(data.request_id);
      setTitle(""); setDescription(""); setWhy("");
      loadMine();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit the request");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      {submitted && (
        <div
          className="border border-emerald-500/30 bg-emerald-500/5 p-5 flex items-start gap-3"
          data-testid="feature-request-success"
        >
          <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
          <div>
            <div className="text-white font-semibold tracking-tight">
              Request logged as <span className="font-mono">{submitted}</span>
            </div>
            <div className="text-xs text-zinc-400 mt-1">
              It's been recorded for review. We don't promise dates — if it gets
              built you'll see it ship.
            </div>
          </div>
        </div>
      )}

      <form onSubmit={submit} className="border border-zinc-800 bg-zinc-900/40 p-6 md:p-8 space-y-6" data-testid="feature-request-form">
        <div>
          <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-amber-400 mb-3">
            <Lightbulb className="w-3 h-3" /> Feature request
          </div>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-white">
            Tell us what should exist
          </h2>
          <p className="text-sm text-zinc-400 mt-2">
            Requests are read and kept. No timelines are promised.
          </p>
        </div>

        <div>
          <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">
            Feature title
          </label>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={140}
            data-testid="feature-title-input"
            placeholder="e.g. Save my caption style as a preset"
            className="bg-zinc-950 border-zinc-800 rounded-none h-11 text-white placeholder:text-zinc-600"
          />
        </div>

        <div>
          <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">
            Description
          </label>
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={4}
            maxLength={4000}
            data-testid="feature-description-input"
            placeholder="What should it do, and where in the product would it live?"
            className="bg-zinc-950 border-zinc-800 rounded-none text-white placeholder:text-zinc-600 resize-none"
          />
        </div>

        <div>
          <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">
            Why would this be useful? <span className="text-zinc-600">(optional)</span>
          </label>
          <Textarea
            value={why}
            onChange={(e) => setWhy(e.target.value)}
            rows={3}
            maxLength={2000}
            data-testid="feature-why-input"
            placeholder="What does it save you, or what does it unblock?"
            className="bg-zinc-950 border-zinc-800 rounded-none text-white placeholder:text-zinc-600 resize-none"
          />
        </div>

        <button
          type="submit"
          disabled={busy}
          data-testid="feature-submit-btn"
          className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest bg-white text-zinc-950 hover:bg-zinc-200 disabled:opacity-50 px-6 py-3 transition-colors"
        >
          {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
          Submit request
        </button>
      </form>

      {mine.length > 0 && (
        <div className="border border-zinc-800 bg-zinc-900/40" data-testid="my-feature-requests">
          <div className="px-6 py-4 border-b border-zinc-800 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
            Your requests
          </div>
          <ul className="divide-y divide-zinc-800">
            {mine.map((r) => (
              <li key={r.request_id} className="px-6 py-4 flex items-center gap-4">
                <span className="font-mono text-[10px] text-zinc-600 shrink-0">{r.request_id}</span>
                <span className="text-sm text-zinc-200 flex-1 truncate">{r.title}</span>
                <span className="text-[10px] font-mono uppercase tracking-widest border border-zinc-700 text-zinc-400 px-2 py-0.5">
                  {r.status}
                </span>
                <span className="text-[10px] font-mono text-zinc-600 hidden sm:inline">
                  {fmtDay(r.created_at)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
